import { useMemo } from 'react';
import { setPathname } from './storeHelpers';
import { runDeferredSiteModelLoadIfNeeded } from './deferredSiteModelLoad';
import { ACCESS_MULTIPLIERS, PRODUCTION_CALCULATION_TYPES, siteCalculationState } from '../services/siteCalculationState';
import { logger } from '../services/loggingService';
import { useViewerStore } from '../store/store';

export const SITE_VIEWER_UNAVAILABLE_TITLE = '3D viewer unavailable';

export const DEFAULT_SITE_VIEWER_MESSAGE =
    'No Aurora/EagleView design data was found for this site. Try reloading the site, or verify in Salesforce that a design has been loaded before continuing.';

/** User-facing message when Aurora/EagleView site data is missing. */
export function siteViewerUnavailableMessage(fallback?: string | null): string {
    const text = fallback?.trim();
    return text || DEFAULT_SITE_VIEWER_MESSAGE;
}

/** True when the 3D site model cannot be opened (load error or missing Aurora/EagleView data). */
export function isSiteViewerUnavailable(): boolean {
    const { siteLoadingError, siteDesigns, loadingCompleted } = useViewerStore.getState();
    if (siteLoadingError?.trim()) return true;
    return Boolean(loadingCompleted && !siteDesigns?.aurora && !siteDesigns?.eagleView);
}

export function getSiteViewerBlockedMessage(): string | null {
    if (!isSiteViewerUnavailable()) return null;
    const { siteLoadingError } = useViewerStore.getState();
    if (siteLoadingError?.trim()) {
        return siteLoadingError.trim();
    }
    return DEFAULT_SITE_VIEWER_MESSAGE;
}

export function isSiteViewer3dBlocked(): boolean {
    return getSiteViewerBlockedMessage() != null;
}

export function showSiteViewerUnavailableModal(message?: string | null): void {
    useViewerStore.setState({
        siteViewerUnavailablePrompt: siteViewerUnavailableMessage(message ?? getSiteViewerBlockedMessage()),
    });
}

export function dismissSiteViewerUnavailableModal(): void {
    useViewerStore.setState({ siteViewerUnavailablePrompt: null });
}

/**
 * Opens the 3D viewer only when site model data loaded successfully.
 * @returns true when navigation ran; false when blocked (modal shown).
 */
export function tryOpenSiteViewer3d(onOpen: () => void): boolean {
    const blockedMessage = getSiteViewerBlockedMessage();
    if (blockedMessage) {
        showSiteViewerUnavailableModal(blockedMessage);
        return false;
    }
    onOpen();
    return true;
}

/** Defer shade/production pipeline while the oneBUTTON dashboard is the active surface. */
export function deferSiteViewerCalculationsOnLoad(): void {
    if (!useViewerStore.getState().isDashboardOpen) return;
    useViewerStore.setState({ siteViewerCalculationsPending: true });
}

export async function startSiteViewerCalculationsFromLoad(): Promise<void> {
    if (useViewerStore.getState().isDashboardOpen) {
        deferSiteViewerCalculationsOnLoad();
        return;
    }
    useViewerStore.setState({ isBgCalc: true });
    siteCalculationState.needsUpdate(ACCESS_MULTIPLIERS, 0);
    try {
        await siteCalculationState.useCalculations(ACCESS_MULTIPLIERS);
    } catch (err) {
        logger.error(err, 'Site viewer calculations failed after load');
    }
}

/** Start shade/production fetches without blocking the drawing overlay. */
export function startSiteViewerCalculationsFromLoadInBackground(): void {
    void startSiteViewerCalculationsFromLoad();
}

/** Run the site calculation chain deferred during dashboard-first load. */
export function runPendingSiteViewerCalculations(): void {
    const { siteViewerCalculationsPending } = useViewerStore.getState();
    if (!siteViewerCalculationsPending) return;
    useViewerStore.setState({ siteViewerCalculationsPending: false, isBgCalc: true });
    siteCalculationState.needsUpdate(ACCESS_MULTIPLIERS, 0);
    void siteCalculationState.useCalculations(ACCESS_MULTIPLIERS).catch((err) => {
        logger.error(err, 'Deferred site viewer calculations failed');
    });
}

function needsDeferredSiteProductionWork(): boolean {
    const {
        deferredSiteModelLoad,
        siteViewerCalculationsPending,
        siteModelLoadPending,
        siteModelLoadInProgress,
    } = useViewerStore.getState();
    return Boolean(
        deferredSiteModelLoad ||
            siteViewerCalculationsPending ||
            siteModelLoadPending ||
            siteModelLoadInProgress,
    );
}

let deferredProductionStart: Promise<boolean> | null = null;

/**
 * Draw the deferred oneDRAW site (if needed) and start shade/production calcs
 * without leaving the dashboard. Used when AI designs needs production values.
 */
export async function startDeferredSiteProductionCalculations(): Promise<boolean> {
    if (isSiteViewer3dBlocked()) return false;
    if (!needsDeferredSiteProductionWork()) return true;
    if (deferredProductionStart) return deferredProductionStart;

    deferredProductionStart = (async () => {
        try {
            const waitStarted = Date.now();
            while (useViewerStore.getState().siteModelLoadInProgress) {
                if (Date.now() - waitStarted > 120_000) break;
                await new Promise((resolve) => setTimeout(resolve, 100));
            }

            const { deferredSiteModelLoad, sceneViewer } = useViewerStore.getState();
            if (deferredSiteModelLoad && !sceneViewer) {
                return false;
            }

            if (deferredSiteModelLoad && sceneViewer) {
                await runDeferredSiteModelLoadIfNeeded();
            }

            runPendingSiteViewerCalculations();
            return true;
        } catch (e) {
            logger.error(e, 'Failed to start deferred site production calculations');
            return false;
        } finally {
            deferredProductionStart = null;
        }
    })();

    return deferredProductionStart;
}

/** Draw deferred oneDRAW site if needed, then wait until production calcs are idle. */
export async function ensureProductionReadyForBackgroundWork(): Promise<void> {
    await startDeferredSiteProductionCalculations();
    const started = Date.now();
    while (true) {
        const state = useViewerStore.getState();
        const stillWaiting =
            state.siteModelLoadPending ||
            state.siteModelLoadInProgress ||
            state.siteViewerCalculationsPending ||
            state.refreshingShade ||
            PRODUCTION_CALCULATION_TYPES.some((type) =>
                siteCalculationState.doesNeedUpdateOrUpdating(type),
            );
        if (!stillWaiting) return;
        if (Date.now() - started > 120_000) {
            logger.warn('Timed out waiting for production calculations');
            return;
        }
        await new Promise((resolve) => setTimeout(resolve, 100));
    }
}

/** Close dashboard and open the roof / 3D configure view (original oneDRAW entry). */
export function openSiteViewer3dFromDashboard(
    setIsDashboardOpen: (next: boolean) => void,
): boolean {
    return tryOpenSiteViewer3d(() => {
        setPathname('/configure/roof');
        setIsDashboardOpen(false);
        void runDeferredSiteModelLoadIfNeeded()
            .catch(() => {
                /* errors logged in deferred loader */
            })
            .finally(() => {
                runPendingSiteViewerCalculations();
            });
    });
}

export function useSiteViewer3dBlocked(): { blocked: boolean; message: string | null } {
    const siteLoadingError = useViewerStore((s) => s.siteLoadingError);
    const siteDesigns = useViewerStore((s) => s.siteDesigns);
    const loadingCompleted = useViewerStore((s) => s.loadingCompleted);

    return useMemo(() => {
        const blocked = Boolean(
            siteLoadingError?.trim() ||
                (loadingCompleted && !siteDesigns?.aurora && !siteDesigns?.eagleView),
        );
        if (!blocked) {
            return { blocked: false, message: null };
        }
        return { blocked: true, message: DEFAULT_SITE_VIEWER_MESSAGE };
    }, [siteLoadingError, siteDesigns?.aurora, siteDesigns?.eagleView, loadingCompleted]);
}
